import React from "react";
import {
  Box,
  Card,
  Typography,
  LinearProgress,
  Skeleton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FunctionsIcon from "@mui/icons-material/Functions";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ScienceIcon from "@mui/icons-material/Science";

const getCategoryIcon = (category) => {
  switch (category) {
    case "Math":
      return <FunctionsIcon />;
    case "Reading":
    case "English":
      return <MenuBookIcon />;
    case "Science":
      return <ScienceIcon />;
    default:
      return <AssignmentIcon />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "Complete":
      return "success.main";
    case "In Progress":
      return "warning.main";
    default:
      return "text.secondary";
  }
};

const AssignmentCard = ({
  a,
  simpleView = false,
  reviewMode = false,
  onView,
}) => {
  const navigate = useNavigate();

  const {
    category,
    title,
    subtitle,
    assignedDate,
    status,
    correct = 0,
    incorrect = 0,
  } = a;

  const totalAnswered = correct + incorrect;
  const percentCorrect =
    totalAnswered > 0
      ? Math.round((correct / totalAnswered) * 100)
      : 0;

  const handleClick = () => {
    if (onView) return onView(a);
    if (!reviewMode && a.testId) {
      navigate(`/test/${a.testId}`, {
        state: { assignment: a },
      });
    }
  };

  if (a.loading) {
    return (
      <Card
        sx={{
          minHeight: 150,
          p: 2,
          borderRadius: 3,
          backgroundColor: "rgba(255,255,255,0.95)",
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        }}
      >
        <Skeleton variant="text" width="70%" height={24} />
        <Skeleton variant="text" width="40%" height={18} />
        <Skeleton variant="text" width="50%" height={18} />
        <Box mt={2}>
          <Skeleton variant="rectangular" height={6} borderRadius={1} />
        </Box>
      </Card>
    );
  }
  
  return (
    <Card
      onClick={handleClick}
      sx={{
        minHeight: 150,
        p: 2,
        borderRadius: 3,
        backgroundColor: "rgba(255,255,255,0.95)",
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        cursor: reviewMode ? "default" : "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": reviewMode
          ? {}
          : {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
            },
      }}
    >
      {/* TOP */}
      <Box>
        <Box display="flex" alignItems="center" mb={0.75}>
          <Box sx={{ mr: 1, color: "text.secondary" }}>
            {getCategoryIcon(category)}
          </Box>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {title}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>

        <Typography variant="body2" mt={0.75}>
          Assigned: {assignedDate}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: getStatusColor(status), mt: 0.25 }}
        >
          Status: {status}
        </Typography>

        {!simpleView && (
          <Box mt={1}>
            {reviewMode ? (
              <Box
                sx={{
                  display: "flex",
                  height: 8,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${percentCorrect}%`,
                    backgroundColor: "success.main",
                  }}
                />
                <Box
                  sx={{
                    width: `${100 - percentCorrect}%`,
                    backgroundColor: "error.main",
                  }}
                />
              </Box>
            ) : (
              <LinearProgress
                variant={
                  status === "Not Attempted"
                    ? "indeterminate"
                    : "determinate"
                }
                value={percentCorrect}
                sx={{ height: 6, borderRadius: 1 }}
              />
            )}
          </Box>
        )}
      </Box>

      {!simpleView && reviewMode && <Box sx={{ height: 8 }} />}

      {!simpleView && reviewMode && (
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <CheckCircleIcon
              fontSize="small"
              sx={{ mr: 0.5, color: "success.main" }}
            />
            <Typography variant="caption">{correct}</Typography>
          </Box>

          <Box display="flex" alignItems="center">
            <CancelIcon
              fontSize="small"
              sx={{ mr: 0.5, color: "error.main" }}
            />
            <Typography variant="caption">{incorrect}</Typography>
          </Box>

          <Typography variant="caption">
            {percentCorrect}%
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default AssignmentCard;
